import { useCallback, useEffect, useRef, useState } from "react";
import { FORCE_PATCH_GAP } from "../config/appUpdate";
import {
    hasReloadedForTarget,
    isNewerVersion,
    loadedAppVersion,
    shouldForceAppUpdate,
} from "../utils/appVersion";

const POLL_INTERVAL_MS = 900_000;
const DISMISSED_VERSION_KEY = "dismissedAppVersion";
/** Set before a forced reload so we fall back to the soft notice if the bundle did not update. */
export const RELOAD_TARGET_STORAGE_KEY = "appUpdateReloadTarget";

async function fetchLatestVersion(): Promise<string | null> {
    try {
        // Query string + no-store: hosting serves version.json with no-cache, but bypass any intermediaries.
        const res = await fetch(`/version.json?_=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return null;
        const data = await res.json();
        return typeof data?.version === "string" ? data.version : null;
    } catch {
        return null;
    }
}

/**
 * Polls `/version.json` and compares against the semver baked into this bundle at build time.
 * Must receive `pathname` from inside a React Router tree (see AppUpdateGate).
 */
export function useAppUpdate(pathname: string) {
    // Dev skips all checks; mark ready so callers do not wait on a fetch that never runs.
    const [ready, setReady] = useState(import.meta.env.DEV);
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [latestVersion, setLatestVersion] = useState<string | null>(null);

    // Stable ref so visibility/focus/interval listeners always call the latest check logic.
    const checkRef = useRef<(() => void) | undefined>(undefined);

    const check = useCallback(async () => {
        if (import.meta.env.DEV) return;

        const latest = await fetchLatestVersion();
        if (!latest) {
            // Fail open: do not block the app or show a false-positive update banner.
            setReady(true);
            setUpdateAvailable(false);
            return;
        }

        setLatestVersion(latest);
        const newer = isNewerVersion(latest, loadedAppVersion);
        // Only clear the reload guard once the bundle reached the stored target (do not clear
        // when version.json matches but Firestore minSupportedVersion still blocks the client).
        const reloadTarget = sessionStorage.getItem(RELOAD_TARGET_STORAGE_KEY);
        if (reloadTarget && !isNewerVersion(reloadTarget, loadedAppVersion)) {
            sessionStorage.removeItem(RELOAD_TARGET_STORAGE_KEY);
        }
        const dismissed = sessionStorage.getItem(DISMISSED_VERSION_KEY);
        // Dismiss is per target version for this tab; a newer deploy clears the prior dismiss.
        setUpdateAvailable(newer && dismissed !== latest);
        setReady(true);
    }, []);

    checkRef.current = check;

    useEffect(() => {
        if (import.meta.env.DEV) return;

        void check();

        const onVisibility = () => {
            if (document.visibilityState === "visible") {
                void checkRef.current?.();
            }
        };
        const onFocus = () => {
            void checkRef.current?.();
        };

        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("focus", onFocus);

        const intervalId = window.setInterval(() => {
            void checkRef.current?.();
        }, POLL_INTERVAL_MS);

        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("focus", onFocus);
            window.clearInterval(intervalId);
        };
    }, [check]);

    // Re-check when the user navigates so long-lived tabs pick up deploys without waiting for the poll interval.
    useEffect(() => {
        if (import.meta.env.DEV) return;
        void checkRef.current?.();
    }, [pathname]);

    const dismissUpdate = useCallback(() => {
        if (!latestVersion) return;
        sessionStorage.setItem(DISMISSED_VERSION_KEY, latestVersion);
        setUpdateAvailable(false);
    }, [latestVersion]);

    const confirmForceReload = useCallback(() => {
        if (!latestVersion) return;
        sessionStorage.setItem(RELOAD_TARGET_STORAGE_KEY, latestVersion);
        window.location.reload();
    }, [latestVersion]);

    const reloadTarget = import.meta.env.DEV
        ? null
        : sessionStorage.getItem(RELOAD_TARGET_STORAGE_KEY);

    const wantsForce =
        !import.meta.env.DEV &&
        updateAvailable &&
        latestVersion !== null &&
        shouldForceAppUpdate({
            latest: latestVersion,
            current: loadedAppVersion,
            pathname,
            forcePatchGap: FORCE_PATCH_GAP,
        });

    // After one reload for this target, stop forcing (e.g. version.json bumped without a new bundle in local preview).
    const forceUpdate = Boolean(
        wantsForce && latestVersion && !hasReloadedForTarget(reloadTarget, latestVersion)
    );

    return { ready, updateAvailable, latestVersion, forceUpdate, dismissUpdate, confirmForceReload };
}
